import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../../auth/AuthContext';
import {
  FaHome,
  FaBox,
  FaShoppingCart,
  FaClipboardList,
  FaBolt,
  FaRocket,
  FaChartLine,
  FaWarehouse,
  FaCog,
  FaSignOutAlt,
  FaTimes,
  FaBalanceScale,
  FaTruck,
  FaCamera,
} from 'react-icons/fa';
import { canUseCameraFirstListing } from '../../../utils/partListingShots';

const Sidebar = ({ isOpen, setIsOpen }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const sellerMode = user?.usedSellerDetails?.sellerMode;
  const dashboardTools = user?.usedSellerDetails?.dashboardTools || [];
  const showYardVehicles =
    ['A', 'B', 'C'].includes(sellerMode) ||
    dashboardTools.includes('yard_inventory') ||
    dashboardTools.includes('bulk_import');
  const showCameraAddPart = canUseCameraFirstListing(user);

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: FaHome, path: '/vendor/dashboard' },
    ...(showCameraAddPart
      ? [{ id: 'parts-add', label: 'Add part', icon: FaCamera, path: '/vendor/dashboard/parts/add' }]
      : []),
    ...(showYardVehicles
      ? [{ id: 'yard', label: 'Yard vehicles', icon: FaTruck, path: '/vendor/dashboard/yard' }]
      : []),
    { id: 'listings-new', label: 'Quick list', icon: FaRocket, path: '/vendor/dashboard/listings/new' },
    { id: 'products', label: 'Products', icon: FaBox, path: '/vendor/dashboard/products' },
    { id: 'orders', label: 'Orders', icon: FaShoppingCart, path: '/vendor/dashboard/orders' },
    { id: 'disputes', label: 'Disputes', icon: FaBalanceScale, path: '/vendor/dashboard/disputes' },
    { id: 'availability', label: 'Availability', icon: FaClipboardList, path: '/vendor/dashboard/availability' },
    { id: 'deals', label: 'Deals', icon: FaBolt, path: '/vendor/dashboard/deals' },
    { id: 'inventory', label: 'Inventory', icon: FaWarehouse, path: '/vendor/dashboard/inventory' },
    { id: 'analytics', label: 'Analytics', icon: FaChartLine, path: '/vendor/dashboard/analytics' },
    { id: 'settings', label: 'Settings', icon: FaCog, path: '/vendor/dashboard/settings' },
  ];

  const isActive = (path) => {
    if (path === '/vendor/dashboard') {
      return location.pathname === '/vendor/dashboard' || location.pathname === '/vendor/dashboard/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-screen dashboard-sidebar z-50 w-64 transform transition-transform duration-300 ease-out ${
          isOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0 lg:sticky lg:top-0 shrink-0`}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between px-5 py-5 border-b border-slate-200/80">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center text-white shadow-lg shadow-emerald-500/25">
                <FaBox className="text-lg" />
              </div>
              <div>
                <h2 className="text-base font-bold text-slate-900">Vendor</h2>
                <p className="text-xs text-slate-500">Seller panel</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="lg:hidden p-2 rounded-lg text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition"
              aria-label="Close menu"
            >
              <FaTimes className="text-lg" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto p-3 scrollbar-hide">
            <p className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">Menu</p>
            <ul className="space-y-1">
              {menuItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                return (
                  <li key={item.id}>
                    <button
                      type="button"
                      onClick={() => { navigate(item.path); setIsOpen(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                        active ? 'bg-emerald-50 text-emerald-700 shadow-sm' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                      }`}
                    >
                      <Icon className={`text-lg shrink-0 ${active ? 'text-emerald-600' : 'text-slate-500'}`} />
                      <span className="truncate">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="p-3 border-t border-slate-200/80 space-y-1">
            <button
              type="button"
              onClick={() => { navigate('/'); setIsOpen(false); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition"
            >
              <FaHome className="text-lg shrink-0" />
              <span>Back to site</span>
            </button>
            <button
              type="button"
              onClick={() => { logout(); navigate('/'); }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-red-600 hover:bg-red-50 transition"
            >
              <FaSignOutAlt className="text-lg shrink-0" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
